import { useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import useUserContext from './useUserContext';
import { Answer, OrderType, Question } from '../types';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  updateQuestions,
  updateAnswer,
  setPageTitle,
  pageTitleSelector,
  searchStringSelector,
  orderSelector,
  setOrder,
  qlistBySearchStringSelector,
  shouldFetchQuestionsSelector,
  setShouldFetchQuestions,
  fetchQuestions,
  fetchTags,
} from '../redux/slices/questionsSlice';
import { setHeaderTitle } from '../redux/slices/layoutSlice';

/**
 * Custom hook for managing the question page state, filtering, and real-time updates.
 *
 * @returns titleText - The current title of the question page
 * @returns qlist - The list of questions to display
 * @returns setQuestionOrder - Function to set the sorting order of questions (e.g., newest, oldest).
 */
const useQuestionPage = () => {
  const { socket } = useUserContext();

  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  const qlist = useAppSelector(qlistBySearchStringSelector) || [];
  const titleText = useAppSelector(pageTitleSelector);
  const searchQuery = useAppSelector(searchStringSelector);
  const tagQuery = searchParams.get('tag') || '';
  const questionOrder = useAppSelector(orderSelector);
  const setQuestionOrder = (order: OrderType) => {
    dispatch(setOrder(order));
    dispatch(setShouldFetchQuestions(true));
  };
  const shouldFetchQuestions = useAppSelector(shouldFetchQuestionsSelector);
  useEffect(() => {
    if (shouldFetchQuestions) {
      dispatch(fetchQuestions(questionOrder));
      dispatch(fetchTags());
      dispatch(setShouldFetchQuestions(false));
    }
  }, [dispatch, questionOrder, shouldFetchQuestions]);

  useEffect(() => {
    dispatch(setHeaderTitle('Questions'));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      setPageTitle(
        searchQuery.length && searchQuery[0] !== '[' && searchQuery[searchQuery.length - 1] !== ']'
          ? `Search Results for "${searchQuery}"`
          : tagQuery || 'All Questions',
      ),
    );
  }, [dispatch, searchQuery, tagQuery]);

  useEffect(() => {
    /**
     * Function to handle question updates from the socket.
     *
     * @param question - the updated question object.
     */
    const handleQuestionUpdate = (question: Question) => {
      dispatch(updateQuestions(question));
    };

    /**
     * Function to handle answer updates from the socket.
     *
     * @param qid - The question ID.
     * @param answer - The answer object.
     */
    const handleAnswerUpdate = ({ qid, answer }: { qid: string; answer: Answer }) => {
      dispatch(updateAnswer({ qid, answer }));
    };

    /**
     * Function to handle views updates from the socket.
     *
     * @param question - The updated question object.
     */
    const handleViewsUpdate = (question: Question) => {
      dispatch(updateQuestions(question));
    };

    socket.on('questionUpdate', handleQuestionUpdate);
    socket.on('answerUpdate', handleAnswerUpdate);
    socket.on('viewsUpdate', handleViewsUpdate);

    return () => {
      socket.off('questionUpdate', handleQuestionUpdate);
      socket.off('answerUpdate', handleAnswerUpdate);
      socket.off('viewsUpdate', handleViewsUpdate);
    };
  }, [dispatch, questionOrder, searchQuery, socket]);

  return { titleText, qlist, questionOrder, setQuestionOrder };
};

export default useQuestionPage;
